import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Reveal } from "@/components/site/Motion";
import { groupDestinations, useTrips } from "@/hooks/useTrips";
import { inr } from "@/lib/api";
import { T } from "@/context/ContentContext";

export function DestinationRow({ region, eyebrow, title, subtitle }) {
  const { trips } = useTrips();
  const dests = groupDestinations(trips, region);
  if (!dests.length) return null;

  return (
    <div className="grid lg:grid-cols-12 gap-8 lg:gap-12 py-14 border-t border-ink/10 first:border-t-0" data-testid={`destination-row-${region}`}>
      <Reveal className="lg:col-span-3">
        <T k={`rows.${region}.eyebrow`} as="p" className="eyebrow text-terra mb-3">{eyebrow}</T>
        <T k={`rows.${region}.title`} as="h3" className="font-display text-3xl sm:text-4xl font-extrabold tracking-tight leading-none">{title}</T>
        <T k={`rows.${region}.subtitle`} as="p" className="font-serif italic text-xl text-stone-600 mt-3">{subtitle}</T>
        <Link to={`/trips?region=${region}`} data-testid={`destination-row-${region}-link`}
          className="inline-block mt-6 text-sm font-semibold underline underline-offset-4 decoration-terra hover:text-terra transition-colors">
          <T k={`rows.${region}.link`}>Explore all</T>
        </Link>
      </Reveal>
      <div className="lg:col-span-9 flex gap-4 sm:gap-6 overflow-x-auto no-scrollbar snap-x -mx-5 px-5 lg:mx-0 lg:px-0 pb-2">
        {dests.map((d, i) => (
          <motion.div key={d.name} className="snap-start shrink-0 w-40 sm:w-48"
            initial={{ opacity: 0, x: 40 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true, amount: 0.1 }}
            transition={{ duration: 0.8, delay: i * 0.07, ease: [0.22, 1, 0.36, 1] }}>
            <Link to={`/trips?destination=${encodeURIComponent(d.name)}`} data-testid={`destination-${d.name.toLowerCase()}`} className="group block">
              <div className="arch overflow-hidden aspect-[3/4]">
                <img src={d.image} alt={d.name} loading="lazy" className="h-full w-full object-cover transition-transform duration-1000 group-hover:scale-110" />
              </div>
              <p className="mt-3 font-display text-lg font-bold group-hover:text-terra transition-colors">{d.name}</p>
              <p className="text-sm text-stone-600">from <span className="font-mono text-ink">{inr(d.from)}</span></p>
            </Link>
          </motion.div>
        ))}
      </div>
    </div>
  );
}

export function DestinationRows() {
  return (
    <section className="bg-paper py-16 sm:py-24" data-testid="destination-rows-section">
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <DestinationRow region="india" eyebrow="India trips" title="Closer to home." subtitle="Mountains, monsoons and royal deserts." />
      </div>
    </section>
  );
}
